import {
  Component,
  computed,
  effect,
  input, output,
  signal,
} from '@angular/core'
import { MatIcon } from '@angular/material/icon'
import { ValidationMessage } from './entity-metadata'
import { EditableTableToolbarButtonComponent } from './editable-table-toolbar-button.component'
import { StringRecord } from '../shared/models/general'

@Component({
  selector: 'app-validation-message-viewer',
  imports: [
    MatIcon,
    EditableTableToolbarButtonComponent,
  ],
  template: `
    <div
        class='message px-2'
        [class.click-enabled]="isMessageClickable()"
        (click)="onMessageClick()"
    >
      <mat-icon fontIcon='error'/>
      <div class='message-text ms-1'>
        {{ selectedMessageText() }}
      </div>
    </div>
    @if (validationMessages().length > 1) {
      <button
          app-editable-table-toolbar-button
          icon='arrow_left'
          [disabled]="!canSelect(-1)"
          (click)="selectMessage(-1)"
          title='Previous message'
      >
        <mat-icon fontIcon='arrow_left'/>
      </button>
      <div class='index-message px-2'>{{ indexMessage() }}</div>
      <button
          app-editable-table-toolbar-button
          icon='arrow_right'
          [disabled]="!canSelect(1)"
          (click)="selectMessage(1)"
          title='Next message'
      >
      </button>
    }
  `,
  styles: `
    :host {
      display: flex;

      .message-text, .index-message {
        min-width: 0;
        display: inline-block;
        padding-top: 8px;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
      
      .message {
        cursor: pointer;
        flex-basis: 0;
        flex-grow: 1;
        display: flex;
        justify-content: flex-end;
        
        &.click-enabled {
          cursor: pointer;
          &:hover {
            text-decoration: underline;
          }
        }
        
        mat-icon {
          color: var(--error-red);
          position: relative;
          top: 5px;
        }
      }
    }
  `,
})
export class ValidationMessageViewerComponent {
  readonly validationMessages = input.required<readonly ValidationMessage[]>()
  readonly labels = input.required<StringRecord>()

  readonly messageClick = output<ValidationMessage>()

  protected readonly messageIndex = signal(0)

  protected readonly selectedMessage = computed((): ValidationMessage | undefined =>
    this.validationMessages()[this.messageIndex()],
  )

  protected readonly selectedMessageText = computed((): string => {
    const message = this.selectedMessage()
    if (message) {
      const field = message.targets[0]?.field
      return field ? `${message.text} (${this.labels()[field] ?? field})` : message.text
    } else {
      return ''
    }
  })

  protected readonly isMessageClickable = computed((): boolean => Boolean(this.getClickableMessage()))

  protected readonly indexMessage = computed((): string => `${1 + this.messageIndex()} of ${this.validationMessages().length}`)

  constructor() {
    effect(() => {
      const maxIndex = Math.max(0, this.validationMessages().length - 1)
      if (this.messageIndex() > maxIndex) {
        this.messageIndex.set(maxIndex)
      }
    })
  }

  protected canSelect(delta: number) {
    const nextIndex = this.messageIndex() + delta
    return nextIndex >= 0 && nextIndex < this.validationMessages().length
  }

  protected selectMessage(delta: number) {
    this.messageIndex.update(_ => _ + delta)
  }

  private getClickableMessage() {
    const message = this.selectedMessage()
    return message?.targets[0]?.field ? message : undefined
  }

  protected onMessageClick() {
    const message = this.getClickableMessage()
    if (message) {
      this.messageClick.emit(message)
    }
  }
}
